import { useEffect, useState } from "react";
import { X as XIcon, Wallet, ArrowDownRight, RotateCcw, Building2, FileText, FileSearch } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate, fmtDateTime, formatIDR, DIVISION_COLOR, buildKetidakpastianTooltip } from "@/lib/constants";

const TABS = [
  { key: "transfers", label: "Transfer Masuk", icon: Wallet },
  { key: "verified", label: "Realisasi Terverifikasi", icon: ArrowDownRight },
  { key: "returns", label: "Pengembalian Kas", icon: RotateCcw },
];

const RETURN_STATUS = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  verified: "bg-emerald-100 text-emerald-800 border-emerald-300",
  rejected: "bg-rose-100 text-rose-800 border-rose-300",
};

const RETURN_LABEL = {
  pending: "Menunggu Verifikasi",
  verified: "Terverifikasi",
  rejected: "Ditolak",
};

/**
 * StaffCashLedgerDialog — drill-down detail semua transfer, realisasi verified, dan
 * pengembalian untuk 1 staff. Dibuka via klik row di `StaffCashRecapWidget` atau klik
 * nama project di dropdown Rekap Saldo Staff (PettyCashPage).
 * (User request 08-Feb-2026): "Staff Ledger Detail: Klik row di StaffCashRecapWidget →
 * dialog detail semua transfer + verified actual + return per staff".
 * 2026-09-23 (user request): prop opsional `projectName` — filter ledger per project.
 * 2026-10-14 (user request — isolasi saldo per proyek): prop `projectId` menjadi
 * identitas utama (stabil); `projectName` tetap diterima sebagai fallback legacy
 * dan dipakai sebagai label tampilan di header dialog.
 */
export default function StaffCashLedgerDialog({ userId, projectId = null, projectName = null, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("transfers");

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setLoading(true);
    // 2026-10-14: project_id (stabil) diprioritaskan; fallback ke project_name
    // untuk pengajuan legacy yang mungkin belum punya project_id.
    const params = [];
    if (projectId) params.push(`project_id=${encodeURIComponent(projectId)}`);
    else if (projectName) params.push(`project_name=${encodeURIComponent(projectName)}`);
    const q = params.length ? `?${params.join("&")}` : "";
    api.get(`/petty-cash/staff-ledger/${userId}${q}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId, projectId, projectName]);

  const s = data?.summary || {};
  const eng = data?.engine || {};
  const sourceTotals = s.source_totals || {};
  const user = data?.user || {};
  const divCls = DIVISION_COLOR[user.division] || "bg-slate-100 text-slate-700 border-slate-300";

  const counts = {
    transfers: data?.transfers?.length || 0,
    verified: data?.verified_actuals?.length || 0,
    returns: data?.returns?.length || 0,
  };

  return (
    <Dialog open={!!userId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="dlg-staff-ledger">
        <DialogHeader>
          <DialogTitle className="font-display text-xl inline-flex items-center gap-2">
            <FileText size={18} className="text-[#FF5A1F]" /> Ledger Kas Staf
          </DialogTitle>
          <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap" data-testid="ledger-engine-note">
            <span>Ringkasan konsisten dengan Rekonsiliasi Kas Staf — transaksi Rp 0 sah & historis tidak diubah.</span>
            <Link
              to="/finance-report/rekonsiliasi-kas-staf"
              data-testid="ledger-open-pc-recon"
              className="inline-flex items-center gap-1 uppercase tracking-wider font-semibold text-[#FF5A1F] hover:underline"
            >
              <FileSearch size={10} /> Rekonsiliasi Kas Staf
            </Link>
          </div>
        </DialogHeader>

        {loading && <div className="py-10 text-center text-slate-400 italic">Memuat ledger…</div>}

        {!loading && data && (
          <div className="space-y-3 text-sm">
            {/* Header identitas + summary */}
            <div className="border border-slate-200 p-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-display text-lg text-[#0B1D3A]" data-testid="ledger-user-name">{user.name}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-2">
                    {user.division && <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase border ${divCls}`}>{user.division}</span>}
                    {user.sub_division && <span className="text-slate-600">{user.sub_division}</span>}
                    <span className="text-slate-400">Lv{user.level}</span>
                  </div>
                  {/* 2026-09-23: badge filter project bila dibuka dari dropdown project. */}
                  {projectName && (
                    <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#0B1D3A] text-white text-[10px] font-semibold" data-testid="ledger-project-filter">
                      Filter Project: {projectName}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2 text-[11px]">
                <div className="border border-slate-200 p-2" title="Dana Diterima = Σ nominal transfer terkonfirmasi + estimasi dana historis (fallback total untuk transfer historis tanpa nominal terkonfirmasi).">
                  <div className="overline text-slate-500 text-[9px]">Dana Diterima</div>
                  <div className="tabular-nums font-bold text-slate-800" data-testid="ledger-tot-transferred">{formatIDR(s.total_transferred)}</div>
                  {eng.has_legacy && (
                    <div className="text-[9px] text-amber-700 mt-0.5" data-testid="ledger-legacy-breakdown">
                      terkonfirmasi {formatIDR(eng.dana_diterima_terkonfirmasi ?? 0)} · estimasi dana historis {formatIDR(eng.dana_diterima_estimasi_legacy ?? 0)}
                    </div>
                  )}
                </div>
                <div className="border border-slate-200 p-2" title="Realisasi Terverifikasi = Σ item yang lolos Verifikasi 1 (verifikasi pengeluaran, per item).">
                  <div className="overline text-slate-500 text-[9px]">− Realisasi Terverifikasi</div>
                  <div className="tabular-nums font-bold text-slate-800">{formatIDR(s.total_verified)}</div>
                  {(eng.realisasi_pending ?? 0) > 0 && (
                    <div className="text-[9px] text-amber-700 mt-0.5" data-testid="ledger-pending-hint">
                      menunggu verifikasi {formatIDR(eng.realisasi_pending)} (belum memotong)
                    </div>
                  )}
                </div>
                <div className="border border-slate-200 p-2" title="Pengembalian Kas Terverifikasi = Σ amount pengembalian kas status terverifikasi (tidak terikat proyek).">
                  <div className="overline text-slate-500 text-[9px]">− Pengembalian Kas Terverifikasi</div>
                  <div className="tabular-nums font-bold text-slate-800">{formatIDR(s.total_returned)}</div>
                </div>
                <div className="border border-amber-300 p-2 bg-amber-50" title="Pengembalian Kas Menunggu Verifikasi = Σ amount yang masih menunggu verifikasi — belum memotong saldo.">
                  <div className="overline text-amber-700 text-[9px]">Pengembalian Kas Menunggu Verifikasi</div>
                  <div className="tabular-nums font-bold text-amber-800">{formatIDR(s.pending_returns)}</div>
                </div>
                <div className={`border-2 p-2 ${s.balance > 0 ? "border-emerald-500 bg-emerald-50" : s.balance < 0 ? "border-rose-500 bg-rose-50" : "border-slate-300 bg-slate-50"}`}
                     title="Saldo Pertanggungjawaban = Dana Diterima − Realisasi Terverifikasi − Pengembalian Kas Terverifikasi.">
                  <div className={`overline text-[9px] ${s.balance > 0 ? "text-emerald-800" : s.balance < 0 ? "text-rose-800" : "text-slate-600"}`}>= Saldo Pertanggungjawaban</div>
                  <div className={`tabular-nums font-black text-lg ${s.balance > 0 ? "text-emerald-700" : s.balance < 0 ? "text-rose-700" : "text-slate-500"}`} data-testid="ledger-balance">{formatIDR(s.balance)}</div>
                  {eng.saldo_pj_completeness === "belum_dapat_dipastikan" && (
                    <div className="text-[9px] text-rose-700 mt-0.5" data-testid="ledger-balance-uncertain"
                         title={`${buildKetidakpastianTooltip(eng)}${eng.saldo_pj_strict != null ? ` Pembanding — tanpa estimasi historis: ${formatIDR(eng.saldo_pj_strict)} · dengan estimasi historis: ${formatIDR(eng.saldo_pj_termasuk_estimasi ?? 0)}.` : ""}`}>
                      Belum dapat dipastikan
                    </div>
                  )}
                  {eng.saldo_pj_completeness === "data_lengkap" && (
                    <div className="text-[9px] text-emerald-700 mt-0.5" data-testid="ledger-balance-complete">Data lengkap</div>
                  )}
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]" data-testid="ledger-source-totals">
                <div className="border border-slate-200 p-2 bg-white">
                  <div className="overline text-slate-500 text-[9px]">Sumber: Cash</div>
                  <div className="tabular-nums font-bold text-slate-800" data-testid="ledger-source-cash">{formatIDR(sourceTotals.cash || 0)}</div>
                </div>
                <div className="border border-slate-200 p-2 bg-white">
                  <div className="overline text-slate-500 text-[9px]">Sumber: Rekening Staf</div>
                  <div className="tabular-nums font-bold text-slate-800" data-testid="ledger-source-staff">{formatIDR(sourceTotals.staff_bank || 0)}</div>
                </div>
                <div className="border border-slate-200 p-2 bg-white">
                  <div className="overline text-slate-500 text-[9px]">Sumber: Rekening Perusahaan</div>
                  <div className="tabular-nums font-bold text-slate-800" data-testid="ledger-source-company">{formatIDR(sourceTotals.company_bank || 0)}</div>
                </div>
                <div className="border border-slate-200 p-2 bg-slate-50">
                  <div className="overline text-slate-500 text-[9px]">Sumber Lama / Belum Diisi</div>
                  <div className="tabular-nums font-bold text-slate-600" data-testid="ledger-source-unknown">{formatIDR(sourceTotals.unknown || 0)}</div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-slate-200 flex gap-0">
              {TABS.map((t) => {
                const active = tab === t.key;
                const Icon = t.icon;
                return (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    data-testid={`ledger-tab-${t.key}`}
                    className={`px-4 py-2 text-[11px] uppercase tracking-wider font-semibold border-b-2 inline-flex items-center gap-1.5 ${active ? "border-[#FF5A1F] text-[#0B1D3A]" : "border-transparent text-slate-500 hover:text-[#0B1D3A]"}`}
                  >
                    <Icon size={11} /> {t.label}
                    <span className={`ml-1 px-1 py-0 text-[9px] tabular-nums ${active ? "bg-[#FF5A1F] text-white" : "bg-slate-200 text-slate-700"}`}>{counts[t.key]}</span>
                  </button>
                );
              })}
            </div>

            {/* Panels */}
            {tab === "transfers" && (
              <div className="overflow-x-auto" data-testid="ledger-panel-transfers">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200 uppercase tracking-wider text-[10px] text-left">
                    <tr>
                      <th className="px-2 py-2">Tgl Transfer</th>
                      <th className="px-2 py-2">Periode / Proyek</th>
                      <th className="px-2 py-2 text-right">Nominal</th>
                      <th className="px-2 py-2">Sumber Dana</th>
                      <th className="px-2 py-2">Rekening Tujuan</th>
                      <th className="px-2 py-2">Diproses Oleh</th>
                      <th className="px-2 py-2">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.transfers.length === 0 && <tr><td colSpan={7} className="px-2 py-6 text-center text-slate-400 italic">Belum ada transfer.</td></tr>}
                    {data.transfers.map((t) => (
                      <tr key={t.pc_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ledger-transfer-${t.pc_id}`}>
                        <td className="px-2 py-2 tabular-nums whitespace-nowrap">{t.transfer_date ? fmtDate(t.transfer_date) : <span className="text-slate-300">—</span>}</td>
                        <td className="px-2 py-2">
                          <div className="font-medium text-[10px] text-slate-700">{t.week_label}</div>
                          <div className="text-[11px] text-[#0B1D3A]">{t.project_name}</div>
                        </td>
                        <td className="px-2 py-2 tabular-nums text-right font-semibold text-slate-800">{formatIDR(t.transfer_amount)}</td>
                        <td className="px-2 py-2 text-[10px]" data-testid={`ledger-transfer-source-${t.pc_id}`}>
                          {t.transfer_source ? (
                            <>
                              <div className="font-medium">
                                {t.transfer_source.type === "cash" ? "Tunai" : t.transfer_source.type === "staff_bank" ? `Staf · ${t.transfer_source.owner_name || "—"}` : "Rekening Perusahaan"}
                              </div>
                              {t.transfer_source.bank_name && <div className="font-mono text-slate-500">{t.transfer_source.bank_name} — {t.transfer_source.account_no}</div>}
                            </>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-2 py-2 text-[10px]">
                          {t.bank_account ? (<>
                            <div className="inline-flex items-center gap-1"><Building2 size={9} /> {t.bank_account.bank_name}</div>
                            <div className="font-mono text-slate-500">{t.bank_account.account_no}</div>
                          </>) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-2 py-2 text-[11px]">{t.transferred_by_name || <span className="text-slate-300">—</span>}</td>
                        <td className="px-2 py-2 text-[10px] italic text-slate-600 max-w-xs truncate" title={t.transfer_note || ""}>{t.transfer_note || <span className="text-slate-300">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "verified" && (
              <div className="overflow-x-auto" data-testid="ledger-panel-verified">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200 uppercase tracking-wider text-[10px] text-left">
                    <tr>
                      <th className="px-2 py-2">Tgl Verifikasi</th>
                      <th className="px-2 py-2">Periode / Proyek</th>
                      <th className="px-2 py-2 text-right">Pengajuan</th>
                      <th className="px-2 py-2 text-right">Aktual</th>
                      <th className="px-2 py-2 text-right">Δ</th>
                      <th className="px-2 py-2">Diverifikasi Oleh</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.verified_actuals.length === 0 && <tr><td colSpan={6} className="px-2 py-6 text-center text-slate-400 italic">Belum ada realisasi terverifikasi.</td></tr>}
                    {data.verified_actuals.map((v) => (
                      <tr key={v.pc_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ledger-verified-${v.pc_id}`}>
                        <td className="px-2 py-2 tabular-nums whitespace-nowrap">{v.verified_at ? fmtDateTime(v.verified_at) : <span className="text-slate-300">—</span>}</td>
                        <td className="px-2 py-2">
                          <div className="font-medium text-[10px] text-slate-700">{v.week_label}</div>
                          <div className="text-[11px] text-[#0B1D3A]">{v.project_name}</div>
                          {v.items?.length > 0 && (
                            <details className="mt-1">
                              <summary className="cursor-pointer text-[9px] uppercase tracking-wider text-slate-500 hover:text-[#0B1D3A]">Lihat {v.items.length} item</summary>
                              <div className="mt-1 pl-2 border-l-2 border-slate-200 space-y-0.5">
                                {v.items.map((it) => (
                                  <div key={it.id} className={`text-[10px] ${it.is_cancelled ? "line-through opacity-60" : ""}`}>
                                    <span className="font-medium">{it.name}</span> · {it.qty}× · <span className="tabular-nums">{formatIDR(it.actual_amount)}</span>
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                        </td>
                        <td className="px-2 py-2 tabular-nums text-right text-slate-600">{formatIDR(v.pc_total)}</td>
                        <td className="px-2 py-2 tabular-nums text-right font-semibold text-slate-800">{formatIDR(v.actual_amount)}</td>
                        <td className={`px-2 py-2 tabular-nums text-right text-[10px] ${v.delta > 0 ? "text-amber-700" : v.delta < 0 ? "text-emerald-700" : "text-slate-400"}`}>{v.delta === 0 ? "—" : (v.delta > 0 ? "▲ " : "▼ ") + formatIDR(Math.abs(v.delta))}</td>
                        <td className="px-2 py-2 text-[11px]">{v.verified_by_name || <span className="text-slate-300">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "returns" && (
              <div className="overflow-x-auto" data-testid="ledger-panel-returns">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200 uppercase tracking-wider text-[10px] text-left">
                    <tr>
                      <th className="px-2 py-2">Tgl Kembali</th>
                      <th className="px-2 py-2 text-right">Nominal</th>
                      <th className="px-2 py-2">Rekening Tujuan</th>
                      <th className="px-2 py-2">Status</th>
                      <th className="px-2 py-2">Diproses</th>
                      <th className="px-2 py-2">Catatan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.returns.length === 0 && <tr><td colSpan={6} className="px-2 py-6 text-center text-slate-400 italic">Belum ada pengembalian.</td></tr>}
                    {data.returns.map((r) => {
                      const st = r.status || "pending";
                      return (
                        <tr key={r.id} className={`border-b border-slate-100 hover:bg-slate-50 ${r.cancelled_at ? "opacity-50 line-through" : ""}`} data-testid={`ledger-return-${r.id}`}>
                          <td className="px-2 py-2 tabular-nums whitespace-nowrap">{fmtDate(r.return_date)}</td>
                          <td className={`px-2 py-2 tabular-nums text-right font-semibold ${st === "verified" ? "text-emerald-700" : st === "rejected" ? "text-slate-500" : "text-amber-700"}`}>{formatIDR(r.amount)}</td>
                          <td className="px-2 py-2 text-[10px]">
                            {r.company_bank_account ? (<>
                              <div className="font-medium">{r.company_bank_account.bank_name}</div>
                              <div className="font-mono text-slate-500">{r.company_bank_account.account_no}</div>
                            </>) : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-2 py-2">
                            <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${RETURN_STATUS[st]}`}>{RETURN_LABEL[st] || st}</span>
                            {r.cancelled_at && <div className="text-[9px] text-rose-700 italic mt-0.5">Dibatalkan</div>}
                          </td>
                          <td className="px-2 py-2 text-[10px]">
                            {st === "verified" && <div className="text-emerald-700">✓ {r.verified_by_name}</div>}
                            {st === "rejected" && <div className="text-rose-700">✗ {r.rejected_by_name}</div>}
                            {st === "pending" && <span className="text-slate-400 italic">menunggu</span>}
                          </td>
                          <td className="px-2 py-2 text-[10px] italic text-slate-600 max-w-xs truncate" title={r.note || r.reject_note || ""}>{r.note || (st === "rejected" && r.reject_note && `Reject: ${r.reject_note}`) || <span className="text-slate-300">—</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
